import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { BookOpen, Layers, FolderOpen, Award, ArrowRight, GraduationCap, ClipboardCheck } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { YEAR_LABEL } from "@/lib/school";
import { t } from "@/lib/i18n";

export default function DocentePortal() {
  const { user } = useAuth();
  const { data: me } = useQuery({
    queryKey: ["teacher-portal-me"],
    queryFn: async () => (await api.get("/teacher-portal/me")).data,
  });
  const { data: courses = [], isLoading } = useQuery({
    queryKey: ["teacher-portal-courses"],
    queryFn: async () => (await api.get("/teacher-portal/courses")).data,
  });
  const { data: sessions = [] } = useQuery({
    queryKey: ["teacher-portal-sessions"],
    queryFn: async () => (await api.get("/teacher-portal/exam-sessions")).data,
  });
  const { data: attendanceSummary = [] } = useQuery({
    queryKey: ["teacher-portal-attendance"],
    queryFn: async () => (await api.get("/teacher-portal/attendance-summary")).data,
  });

  const openSessions = sessions.filter((s) => s.status === "aperto").length;
  const upcomingSessions = sessions.filter((s) => s.date >= new Date().toLocaleDateString("en-CA")).slice(0, 4);

  return (
    <div className="p-4 sm:p-6 md:p-10 max-w-[1400px] mx-auto">
      <PageHeader
        title={`${t("Portale docente")} — ${me?.first_name || user?.name?.split(" ")[0] || ""}`}
        subtitle="I tuoi corsi, materiali didattici e appelli d'esame in un unico spazio."
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <Card className="p-5 border border-stone-200 rounded-xl shadow-none">
          <div className="text-[10px] uppercase tracking-widest text-stone-500 flex items-center gap-1.5"><BookOpen size={11}/> Corsi</div>
          <div className="text-2xl font-semibold mt-1 text-[#1C352D]" data-testid="docente-courses-count">{courses.length}</div>
        </Card>
        <Card className="p-5 border border-stone-200 rounded-xl shadow-none">
          <div className="text-[10px] uppercase tracking-widest text-stone-500 flex items-center gap-1.5"><FolderOpen size={11}/> Materiali</div>
          <div className="text-2xl font-semibold mt-1">{courses.reduce((s, c) => s + (c.materials_count || 0), 0)}</div>
        </Card>
        <Card className="p-5 border border-stone-200 rounded-xl shadow-none">
          <div className="text-[10px] uppercase tracking-widest text-stone-500 flex items-center gap-1.5"><Award size={11}/> Appelli aperti</div>
          <div className="text-2xl font-semibold mt-1 text-emerald-700">{openSessions}</div>
        </Card>
        <Card className="p-5 border border-stone-200 rounded-xl shadow-none">
          <div className="text-[10px] uppercase tracking-widest text-stone-500 flex items-center gap-1.5"><Layers size={11}/> Moduli</div>
          <div className="text-2xl font-semibold mt-1">{courses.reduce((s, c) => s + (c.modules_count || 0), 0)}</div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <Card className="border border-stone-200 rounded-xl shadow-none">
            <div className="p-5 border-b border-stone-100 flex items-center justify-between">
              <div className="text-sm font-medium flex items-center gap-2"><BookOpen size={14}/> I miei corsi</div>
              <Link to="/scuola/corsi" className="text-xs text-[#E2725B] hover:underline">Catalogo completo</Link>
            </div>
            {isLoading ? (
              <div className="p-8 text-sm text-stone-400 text-center">Caricamento…</div>
            ) : courses.length === 0 ? (
              <div className="p-8 text-sm text-stone-400 text-center">
                Nessun corso assegnato. Contatta la segreteria per essere co-titolarizzato in un corso.
              </div>
            ) : (
              <ul className="divide-y divide-stone-100">
                {courses.map((c) => (
                  <li key={c.id}>
                    <Link
                      to={`/scuola/corsi/${c.id}`}
                      data-testid={`docente-course-${c.id}`}
                      className="p-4 flex items-center gap-3 hover:bg-stone-50/60 transition-colors"
                    >
                      <div className="w-1 h-10 rounded-full" style={{ background: c.area_color || "#1C352D" }}/>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <div className="font-medium truncate">{c.name}</div>
                          {c.year_level && <Badge className="bg-[#1C352D] text-white text-[10px] rounded-full">{YEAR_LABEL[c.year_level]}</Badge>}
                          {c.is_seminar && <Badge className="bg-[#E2725B] text-white text-[10px] rounded-full">SEMINARIO</Badge>}
                        </div>
                        <div className="text-[11px] text-stone-500 mt-0.5 flex items-center gap-3">
                          {c.area_name && <span>{c.area_name}</span>}
                          {c.cfu != null && <span>{c.cfu} CFU</span>}
                          <span>· {c.modules_count} moduli</span>
                          <span>· {c.materials_count} materiali</span>
                        </div>
                      </div>
                      <ArrowRight size={16} className="text-stone-400"/>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>

        <Card className="border border-stone-200 rounded-xl shadow-none">
          <div className="p-5 border-b border-stone-100">
            <div className="text-sm font-medium flex items-center gap-2"><Award size={14}/> Prossimi appelli</div>
          </div>
          {upcomingSessions.length === 0 ? (
            <div className="p-6 text-sm text-stone-400 text-center">Nessun appello in programma.</div>
          ) : (
            <ul className="divide-y divide-stone-100">
              {upcomingSessions.map((s) => (
                <li key={s.id} className="p-4">
                  <Link to={`/scuola/esami/${s.id}`} className="block hover:text-[#1C352D]">
                    <div className="text-xs font-medium">{s.course_name || "Corso"}</div>
                    <div className="text-[11px] text-stone-500 mt-0.5">
                      {s.date} {s.time?.slice(0, 5)} · {s.bookings_count} prenotati · {s.grades_count} voti
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>

      <Card className="p-5 mt-6 border border-stone-200 rounded-xl shadow-none bg-gradient-to-br from-[#1C352D] to-[#264A3E] text-white">
        <div className="flex items-start gap-3">
          <GraduationCap size={20}/>
          <div>
            <div className="text-sm font-medium">Come funziona il portale</div>
            <div className="text-xs opacity-80 mt-1 leading-relaxed">
              Da ogni corso puoi caricare materiale didattico (slide, PDF, link video), gestire i moduli e verbalizzare i voti degli appelli assegnati. Gli studenti iscritti al tuo anno/percorso vedranno automaticamente i materiali nel loro portale.
            </div>
          </div>
        </div>
      </Card>

      {/* Presenze per corso del docente */}
      <Card className="mt-6 border border-stone-200 rounded-xl shadow-none" data-testid="docente-attendance-summary">
        <div className="p-5 border-b border-stone-100 text-sm font-medium flex items-center gap-2">
          <ClipboardCheck size={14}/> Presenze per corso
        </div>
        {attendanceSummary.length === 0 ? (
          <div className="p-6 text-sm text-stone-400 text-center">
            Nessuna sessione presenza registrata sui tuoi corsi.
          </div>
        ) : (
          <ul className="divide-y divide-stone-100">
            {attendanceSummary.map((c) => (
              <li key={c.course_id} className="p-4" data-testid={`docente-att-${c.course_id}`}>
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-medium truncate">{c.course_name}</div>
                      {c.is_seminar && <Badge className="bg-[#E2725B] text-white text-[10px] rounded-full">SEMINARIO</Badge>}
                      {c.year_level && <Badge className="bg-[#1C352D] text-white text-[10px] rounded-full">{YEAR_LABEL[c.year_level]}</Badge>}
                    </div>
                    <div className="text-[11px] text-stone-500 mt-0.5 flex items-center gap-3 flex-wrap">
                      <span>{c.total_sessions} lezioni</span>
                      <span>· {c.students_count} studenti</span>
                      {c.counts?.assente > 0 && <span className="text-red-700">· {c.counts.assente} assenze</span>}
                      {c.counts?.ritardo > 0 && <span className="text-orange-700">· {c.counts.ritardo} ritardi</span>}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-2xl font-semibold text-[#1C352D]">{c.attendance_rate}%</div>
                    <div className="text-[10px] uppercase tracking-widest text-stone-500">tasso presenza</div>
                  </div>
                </div>
                <div className="h-1.5 bg-stone-100 rounded-full overflow-hidden mt-2">
                  <div className="h-full rounded-full transition-all" style={{ width: `${c.attendance_rate}%`, background: c.attendance_rate >= 70 ? "#16a34a" : c.attendance_rate >= 50 ? "#f59e0b" : "#dc2626" }}/>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}
